/**
 * Public Booking Page — /meet/:username
 * No auth required. Cal.com-style slot picker.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './BookingPage.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const BookingPage = () => {
  const { username } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState('date'); // date | form | done
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [booking, setBooking] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const appBaseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://app.numfree.com';

  useEffect(() => {
    fetch(`/api/calendar/booking/config/${username}`)
      .then(r => r.ok ? r.json() : Promise.reject('not found'))
      .then(data => { setConfig(data); setLoading(false); })
      .catch(() => { setError('Booking page not found'); setLoading(false); });
  }, [username]);

  const loadSlots = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setLoadingSlots(true);
    const dateStr = date.toISOString().slice(0, 10);
    fetch(`/api/calendar/booking/slots/${username}?date=${dateStr}`)
      .then(r => r.json())
      .then(data => { setSlots((data.slots || []).filter(s => s.available)); setLoadingSlots(false); })
      .catch(() => { setSlots([]); setLoadingSlots(false); });
  };

  const handleBook = async () => {
    if (!form.name || !form.email) return;
    setBooking(true);
    try {
      const res = await fetch(`/api/calendar/booking/book/${username}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slot: selectedSlot.start,
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          notes: form.notes || undefined
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setConfirmation(data);
        setStep('done');
      } else {
        alert(data.error || 'Booking failed');
      }
    } catch (e) {
      alert('Failed to book. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  // Calendar grid
  const renderCalendar = () => {
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cells = [];

    for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} className="bcal-cell empty" />);

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(calYear, calMonth, d);
      const isPast = date < today;
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
      const isToday = date.toDateString() === today.toDateString();
      cells.push(
        <button
          key={d}
          className={`bcal-cell ${isPast ? 'past' : ''} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
          disabled={isPast}
          onClick={() => loadSlots(date)}
        >
          {d}
        </button>
      );
    }
    return cells;
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };

  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };

  const formatSlotTime = (isoStr) => {
    const d = new Date(isoStr);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  if (loading) return <div className="booking-page"><div className="booking-loading">Loading...</div></div>;
  if (error) return <div className="booking-page"><div className="booking-error">{error}</div></div>;

  return (
    <div className="booking-page">
      <div className="booking-container">
        {/* Left — Host info */}
        <div className="booking-host">
          <div className="host-avatar">{config.name ? config.name[0].toUpperCase() : '?'}</div>
          <h1 className="host-name">{config.name}</h1>
          <h2 className="booking-title">{config.title}</h2>
          {config.description && <p className="booking-desc">{config.description}</p>}
          <div className="booking-meta">
            <span className="meta-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {config.duration} min
            </span>
            <span className="meta-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
              {config.timezone}
            </span>
          </div>
        </div>

        {/* Right — Picker */}
        <div className="booking-picker">
          {step === 'date' && (
            <>
              <h3>Select a date & time</h3>
              <div className="booking-cal-nav">
                <button onClick={prevMonth}>&lt;</button>
                <span>{MONTHS[calMonth]} {calYear}</span>
                <button onClick={nextMonth}>&gt;</button>
              </div>
              <div className="booking-cal-grid">
                {WEEKDAYS.map(d => <div key={d} className="bcal-header">{d}</div>)}
                {renderCalendar()}
              </div>

              {selectedDate && (
                <div className="slots-panel">
                  <h4>{selectedDate.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}</h4>
                  {loadingSlots ? (
                    <p className="slots-loading">Loading slots...</p>
                  ) : slots.length === 0 ? (
                    <p className="slots-empty">No available slots on this day</p>
                  ) : (
                    <div className="slots-list">
                      {slots.map((s, i) => (
                        <button
                          key={i}
                          className={`slot-btn ${selectedSlot === s ? 'selected' : ''}`}
                          onClick={() => setSelectedSlot(s)}
                        >
                          {formatSlotTime(s.start)}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedSlot && (
                    <button className="btn-continue" onClick={() => setStep('form')}>
                      Continue &rarr;
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {step === 'form' && (
            <div className="booking-form">
              <button className="btn-back" onClick={() => setStep('date')}>&larr; Back</button>
              <h3>Your details</h3>
              <p className="form-summary">
                {selectedDate.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })} at {formatSlotTime(selectedSlot.start)} ({config.duration} min)
              </p>
              <div className="bform-group">
                <label>Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" autoFocus />
              </div>
              <div className="bform-group">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
              </div>
              <div className="bform-group">
                <label>Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 (555) 000-0000" />
              </div>
              <div className="bform-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Anything you'd like to discuss?" />
              </div>
              <button className="btn-book" onClick={handleBook} disabled={booking || !form.name || !form.email}>
                {booking ? 'Booking...' : 'Confirm Booking'}
              </button>
            </div>
          )}

          {step === 'done' && (
            <div className="booking-done">
              <div className="done-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <h3>You're booked!</h3>
              <p className="done-detail">
                {selectedDate.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })} at {formatSlotTime(selectedSlot.start)}
              </p>
              <p className="done-detail">with {config.name} ({config.duration} min)</p>
              <p className="done-note">A confirmation has been sent to {form.email}</p>
            </div>
          )}
        </div>
      </div>

      <div className="booking-footer">
        <a href={appBaseUrl}>Powered by NumFree</a>
      </div>
    </div>
  );
};

export default BookingPage;
